import { useEffect, useState } from "react";
import { Download, Eye, FileText, RefreshCw, Search, Send, X, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate, fmtTRY } from "../../lib/format";

const STATUS_STYLE = {
  DRAFT: "bg-neutral-500/15 text-neutral-400",
  PENDING: "bg-amber-500/15 text-amber-400",
  SENT: "bg-blue-500/15 text-blue-400",
  ACCEPTED: "bg-emerald-500/15 text-emerald-400",
  REJECTED: "bg-rose-500/15 text-rose-400",
  FAILED: "bg-rose-500/15 text-rose-400",
  CANCELLED: "bg-neutral-500/15 text-neutral-500",
};
const STATUS_LABEL = { DRAFT: "Taslak", PENDING: "Bekliyor", SENT: "Gönderildi", ACCEPTED: "Onaylandı", REJECTED: "Reddedildi", FAILED: "Hatalı", CANCELLED: "İptal" };
const TYPE_LABEL = { E_INVOICE: "e-Fatura", E_ARCHIVE: "e-Arşiv", PENDING_CHECK: "Kontrol Bekliyor" };

export default function InvoicesPage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);

  const load = () => {
    const params = new URLSearchParams({ page: String(page), limit: "30" });
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (type) params.set("invoice_type", type);
    api.get(`/admin/invoices?${params}`).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [q, status, type, page]);

  const openDetail = async (invoiceId) => {
    const { data } = await api.get(`/admin/invoices/${invoiceId}`);
    setDetail(data);
  };

  const act = async (fn, okMsg) => {
    try {
      await fn();
      toast.success(okMsg);
      if (detail) openDetail(detail.invoice_id);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCsv = async () => {
    const { data } = await api.get("/admin/invoices/export");
    const blob = new Blob(["\uFEFF" + data.csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "bigeldi-faturalar.csv";
    a.click();
    toast.success(`${data.count} fatura dışa aktarıldı`);
  };

  const selectCls = "h-10 px-3 bg-[#181B20] border border-[#272B33] rounded-xl text-sm text-white focus:outline-none focus:border-blue-500";
  const canRetry = detail && ["DRAFT", "PENDING", "FAILED", "REJECTED"].includes(detail.status);

  return (
    <div className="space-y-5" data-testid="admin-invoices">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display font-extrabold text-2xl text-white">Faturalar <span className="text-sm text-neutral-500 font-normal">({data?.total ?? 0})</span></h1>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Fatura no, sipariş, müşteri, VKN..."
              className="h-10 pl-9 pr-4 bg-[#181B20] border border-[#272B33] rounded-xl text-sm text-white w-64 focus:outline-none focus:border-blue-500" data-testid="invoice-search" />
          </div>
          <select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} className={selectCls} data-testid="invoice-type-filter">
            <option value="">Tüm Tipler</option>
            <option value="E_INVOICE">e-Fatura</option>
            <option value="E_ARCHIVE">e-Arşiv</option>
            <option value="PENDING_CHECK">Kontrol Bekliyor</option>
          </select>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className={selectCls} data-testid="invoice-status-filter">
            <option value="">Tüm Durumlar</option>
            {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button onClick={exportCsv} className="h-10 px-4 rounded-xl bg-[#181B20] border border-[#272B33] text-sm text-white font-semibold flex items-center gap-2 hover:border-blue-500" data-testid="invoice-export">
            <Download size={15} /> CSV
          </button>
        </div>
      </div>

      <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]" data-testid="invoices-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 border-b border-[#272B33]">
              <th className="px-5 py-3.5">Fatura No</th><th className="px-4 py-3.5">Sipariş</th><th className="px-4 py-3.5">Müşteri</th>
              <th className="px-4 py-3.5">Tip</th><th className="px-4 py-3.5">Tarih</th><th className="px-4 py-3.5">Tutar</th>
              <th className="px-4 py-3.5">Durum</th><th className="px-4 py-3.5 text-right">Detay</th>
            </tr>
          </thead>
          <tbody>
            {data?.items?.map((inv) => (
              <tr key={inv.invoice_id} className="border-b border-[#272B33]/50 hover:bg-[#1E222A]/50" data-testid={`invoice-row-${inv.invoice_id}`}>
                <td className="px-5 py-3 font-mono-ed text-white">{inv.invoice_number || "—"}{inv.is_test && <span className="ml-2 text-[9px] text-amber-500 font-bold">TEST</span>}</td>
                <td className="px-4 py-3 font-mono-ed text-xs text-neutral-400">{inv.order_number}</td>
                <td className="px-4 py-3">
                  <p className="text-white">{inv.customer_company_name || inv.customer_name}</p>
                  <p className="text-[11px] text-neutral-500">{inv.customer_tax_number || inv.customer_identity_number || inv.customer_email}</p>
                </td>
                <td className="px-4 py-3 text-xs text-neutral-300">{TYPE_LABEL[inv.invoice_type] || inv.invoice_type}</td>
                <td className="px-4 py-3 text-xs text-neutral-400">{fmtDate(inv.issue_date || inv.created_at)}</td>
                <td className="px-4 py-3 font-mono-ed font-semibold text-white">{fmtTRY(inv.grand_total)}</td>
                <td className="px-4 py-3"><span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[inv.status] || ""}`} data-testid="invoice-status-badge">{STATUS_LABEL[inv.status] || inv.status}</span></td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openDetail(inv.invoice_id)} className="p-2 rounded-lg text-neutral-400 hover:text-blue-400 hover:bg-blue-500/10" data-testid="invoice-view"><Eye size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.items?.length === 0 && <p className="text-center text-neutral-500 text-sm py-12">Fatura bulunamadı</p>}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50" data-testid="invoice-detail-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDetail(null)} />
          <div className="absolute right-0 inset-y-0 w-full max-w-xl bg-[#13161B] border-l border-[#272B33] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-mono-ed font-bold text-xl text-white">{detail.invoice_number || "Henüz numaralanmadı"}</h2>
                <p className="text-xs text-neutral-500">{TYPE_LABEL[detail.invoice_type]} · {detail.order_number}</p>
              </div>
              <button onClick={() => setDetail(null)} className="p-2 text-neutral-400 hover:text-white" data-testid="invoice-detail-close"><X size={20} /></button>
            </div>

            <div className="bg-[#181B20] border border-[#272B33] rounded-xl p-4 mb-4 text-xs space-y-1.5">
              <div className="flex justify-between"><span className="text-neutral-500">Durum</span><span className={`font-bold px-2 py-0.5 rounded-full ${STATUS_STYLE[detail.status]}`} data-testid="invoice-detail-status">{STATUS_LABEL[detail.status]}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">UUID (ETTN)</span><span className="font-mono-ed text-white">{detail.invoice_uuid || "—"}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">QNB Belge ID</span><span className="font-mono-ed text-white">{detail.qnb_document_id || "—"}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Sağlayıcı</span><span className="text-white">{detail.provider}{detail.is_test && " (TEST)"}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Müşteri</span><span className="text-white">{detail.customer_company_name || detail.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">VKN/TCKN</span><span className="font-mono-ed text-white">{detail.customer_tax_number || detail.customer_identity_number || "—"}</span></div>
              {detail.error_message && <p className="text-rose-400 pt-1">{detail.error_code}: {detail.error_message}</p>}
            </div>

            <div className="space-y-2 mb-4">
              {detail.items?.map((it) => (
                <div key={it.invoice_item_id} className="flex justify-between bg-[#181B20] border border-[#272B33] rounded-xl p-3 text-xs">
                  <div>
                    <p className="text-white font-medium">{it.product_name}</p>
                    <p className="text-neutral-500 font-mono-ed text-[10px]">{it.sku} · {it.barcode} · {it.quantity} adet · KDV %{it.tax_rate}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-mono-ed">{fmtTRY(it.line_total)}</p>
                    <p className="text-neutral-500 text-[10px]">KDV: {fmtTRY(it.tax_amount)}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-xs space-y-1.5 mb-6 bg-[#181B20] border border-[#272B33] rounded-xl p-4">
              <div className="flex justify-between text-neutral-400"><span>Ara toplam</span><span>{fmtTRY(detail.subtotal)}</span></div>
              <div className="flex justify-between text-emerald-400"><span>İndirim</span><span>-{fmtTRY(detail.discount_total)}</span></div>
              <div className="flex justify-between text-neutral-400"><span>Kargo</span><span>{detail.shipping_total === 0 ? "0,00 ₺ (ücretsiz)" : fmtTRY(detail.shipping_total)}</span></div>
              <div className="flex justify-between text-neutral-400"><span>KDV</span><span>{fmtTRY(detail.tax_total)}</span></div>
              <div className="flex justify-between text-white font-bold text-sm pt-2 border-t border-[#272B33]"><span>Genel Toplam</span><span className="font-mono-ed">{fmtTRY(detail.grand_total)}</span></div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {detail.invoice_pdf_url && <a href={detail.invoice_pdf_url} target="_blank" rel="noreferrer" className="h-10 rounded-xl bg-[#181B20] border border-[#272B33] text-xs font-semibold text-white flex items-center justify-center gap-2 hover:border-blue-500" data-testid="invoice-pdf-btn"><FileText size={14} /> PDF Görüntüle</a>}
              {detail.invoice_xml_url && <a href={detail.invoice_xml_url} target="_blank" rel="noreferrer" className="h-10 rounded-xl bg-[#181B20] border border-[#272B33] text-xs font-semibold text-white flex items-center justify-center gap-2 hover:border-blue-500" data-testid="invoice-xml-btn"><FileText size={14} /> XML Görüntüle</a>}
              {detail.qnb_document_id && (
                <button onClick={() => act(() => api.post(`/admin/invoices/${detail.invoice_id}/status-query`), "QNB durumu güncellendi")}
                  className="h-10 rounded-xl bg-blue-600/15 text-blue-400 text-xs font-semibold flex items-center justify-center gap-2 hover:bg-blue-600/25" data-testid="invoice-status-query">
                  <RefreshCw size={14} /> QNB Durumunu Sorgula
                </button>
              )}
              {canRetry && (
                <button onClick={() => act(() => api.post(`/admin/invoices/${detail.invoice_id}/retry`), "Fatura tekrar gönderildi")}
                  className="h-10 rounded-xl bg-emerald-600/15 text-emerald-400 text-xs font-semibold flex items-center justify-center gap-2 hover:bg-emerald-600/25" data-testid="invoice-retry-btn">
                  <Send size={14} /> Tekrar Gönder
                </button>
              )}
              {detail.invoice_type === "PENDING_CHECK" && (
                <button onClick={() => act(() => api.post(`/admin/invoices/${detail.invoice_id}/mark-efatura-verified`, { note: "Admin onayı" }), "e-Fatura olarak işaretlendi ve gönderildi")}
                  className="h-10 rounded-xl bg-purple-600/15 text-purple-400 text-xs font-semibold col-span-2 flex items-center justify-center gap-2" data-testid="invoice-efatura-verify">
                  e-Fatura Mükellefiyeti Doğrulandı — Gönder
                </button>
              )}
              {detail.status !== "CANCELLED" && (
                <button onClick={() => { if (window.confirm("Fatura iptal edilsin mi? (Ödeme iadesi ayrı işlemdir)")) act(() => api.post(`/admin/invoices/${detail.invoice_id}/cancel`, { reason: "Admin iptali" }), "Fatura iptal edildi"); }}
                  className="h-10 rounded-xl bg-rose-600/15 text-rose-400 text-xs font-semibold col-span-2 flex items-center justify-center gap-2 hover:bg-rose-600/25" data-testid="invoice-cancel-btn">
                  <XCircle size={14} /> Faturayı İptal Et
                </button>
              )}
            </div>

            {detail.request_logs?.length > 0 && (
              <div className="mt-5 pt-4 border-t border-[#272B33]">
                <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">Sağlayıcı İstek Kayıtları</p>
                {detail.request_logs.map((l) => (
                  <p key={l.log_id} className="text-[10px] font-mono-ed text-neutral-500">
                    {fmtDate(l.request_at)} · {l.provider} · {l.ok ? "OK" : "HATA"} · {l.status}{l.error_code ? ` · ${l.error_code}` : ""}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
